import { useRingGroups, useQueues } from '../../lib/api/hooks-tms.js';
import { useSession } from '../../lib/auth/session.js';
import { useExtensionFeature } from '../../lib/api/hooks-extension.js';
import { useTenant, useUpdateExtension } from '../../lib/api/hooks.js';
import { useRealtime } from '../../lib/api/realtime.js';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { Empty, Badge, ErrorBanner, SectionHead, CardHead } from '../../components/ui/Primitives.js';
import { SipCredential } from './SipCredential.js';
import { ExtensionProvisioningTab } from '../provisioning/ExtensionProvisioning.js';
import { MobileProvisioning } from '../provisioning/MobileProvisioning.js';
import { AppSignIn } from '../provisioning/AppSignIn.js';
import { AdditionalRegistrations } from '../provisioning/AdditionalRegistrations.js';
import { Icon } from '../../components/ui/Icon.js';
import { relativeTime } from '../../lib/format.js';
import type { Extension } from '@arcnet/contracts';

/**
 * Where this extension is used.
 *
 * Read-only on purpose: membership is a property of the hunt list or queue, and
 * editing it from both ends produces two screens that can disagree about the
 * same row. Links point at the owning object instead.
 */
/**
 * This agent's own wrap-up, where it differs from the queues they take calls for (0111).
 *
 * Here rather than on the queue, because it is about the person: a trainee held longer than the
 * floor, a senior agent trusted to be quick. Null is not zero -- null inherits, zero is somebody
 * genuinely given none -- so each setting has an explicit "from their queues" choice rather than
 * an empty box that could mean either.
 *
 * ADMIN-ONLY by the permission it saves with (extension:write). An agent has extension:write:own,
 * which the API does not accept for these fields: how long somebody is held after a call is not
 * a thing they decide for themselves.
 */
function WrapUpOverride({ tenantId, ext, queues }: {
  tenantId: string;
  ext: Extension;
  queues: ReadonlyArray<{ name: string; wrapup_sec: number; require_disposition?: boolean | undefined }>;
}) {
  const can = useSession((s) => s.can);
  const save = useUpdateExtension(tenantId);
  if (!can('extension:write')) return null;

  const fromQueues = queues.length > 0
    ? `${Math.max(...queues.map((q) => q.wrapup_sec))}s, ${queues.some((q) => q.require_disposition) ? 'must mark' : 'no mark needed'}`
    : 'nothing, so the account\u2019s own setting applies';

  const secs = ext.wrap_up_sec ?? null;
  const mark = ext.require_disposition ?? null;

  return (
    <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
      <SectionHead title="Wrap-up for this agent"
        sub={`Their queues ask for ${fromQueues}`} />
      <div className="arc-row" style={{ gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
        <div className="arc-field" style={{ margin: 0, minWidth: 190 }}>
          <label className="arc-label" htmlFor="wrap-secs">Wrap-up time</label>
          <select id="wrap-secs" className="arc-select" value={secs === null ? '' : String(secs)}
            onChange={(e) => save.mutate({
              id: ext.id,
              wrap_up_sec: e.target.value === '' ? null : Number(e.target.value),
            })}>
            <option value="">From their queues</option>
            <option value="0">None</option>
            <option value="15">15 seconds</option>
            <option value="30">30 seconds</option>
            <option value="60">1 minute</option>
            <option value="120">2 minutes</option>
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0, minWidth: 220 }}>
          <label className="arc-label" htmlFor="wrap-mark">Marking a call</label>
          <select id="wrap-mark" className="arc-select" value={mark === null ? '' : mark ? 'yes' : 'no'}
            onChange={(e) => save.mutate({
              id: ext.id,
              require_disposition: e.target.value === '' ? null : e.target.value === 'yes',
            })}>
            <option value="">From their queues</option>
            <option value="yes">Must mark before going ready</option>
            <option value="no">Never required</option>
          </select>
        </div>
      </div>
      {save.error && <ErrorBanner error={save.error} />}
    </div>
  );
}

export function MembershipTab({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const groups = useRingGroups(tenantId);
  const queues = useQueues(tenantId);

  const inGroups = (groups.data?.data ?? []).filter(
    (g) => g.members.some((m) => m.extension_id === ext.id));
  const inQueues = (queues.data?.data ?? []).filter(
    (q) => q.agents.some((a) => a.extension_id === ext.id));

  const nothing = inGroups.length === 0 && inQueues.length === 0;

  return (
    <>
      <WrapUpOverride tenantId={tenantId} ext={ext} queues={inQueues} />

      <p className="arc-note">
        Membership is edited on the hunt list or queue itself, so there is one
        place that decides who is in it.
      </p>

      {nothing ? (
        <div className="arc-card">
          <Empty icon="users" title="Not in any hunt list or queue"
            hint="This extension only receives calls dialled directly or routed to it by name." />
        </div>
      ) : (
        <div className="arc-card">
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr><th>Type</th><th>Name</th><th>Dial code</th><th>Detail</th></tr>
              </thead>
              <tbody>
                {inGroups.map((g) => (
                  <tr key={g.id}>
                    <td>hunt list</td>
                    <td style={{ fontWeight: 550 }}>{g.name}</td>
                    <td className="arc-mono">{g.extension ?? '—'}</td>
                    <td className="arc-muted">
                      {g.strategy.replace(/_/g, ' ')} · {g.members.length} members
                    </td>
                  </tr>
                ))}
                {inQueues.map((q) => {
                  const agent = q.agents.find((a) => a.extension_id === ext.id);
                  return (
                    <tr key={q.id}>
                      <td>queue</td>
                      <td style={{ fontWeight: 550 }}>{q.name}</td>
                      <td className="arc-mono">{q.extension ?? '—'}</td>
                      <td className="arc-muted">
                        tier {agent?.tier_level ?? 1} · {q.agents.length} agents
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Everything needed to get a phone working on this seat.
 *
 * Somebody opens this with one of three jobs: type the account into a
 * softphone, add a desk phone by its MAC address, or scan the app's QR code.
 * Those were split between a Registration tab and a Provisioning tab, both past
 * the end of a tab strip too long to show them, so the password, the MAC field
 * and the QR code all looked as though they had gone (14 September 2026). One
 * section now, starting with what somebody types into a phone by hand.
 *
 * Cards of one shape since 15 September 2026, in the order the job goes: what
 * any phone signs in with, then desk phones. A loose heading with a large empty
 * state between two cards read as three unrelated things. The app's setup code
 * is part of the first since 16 September: it carries the same details.
 */
export function PhoneSetupTab({ tenantId, ext, selfService = false }: {
  tenantId: string; ext: Extension;
  /**
   * The seat's own person, setting up their own phone (owner, 23 September 2026).
   *
   * The app's sign-in code only. The rest of this tab is the provider's work -- the SIP password
   * in plain sight, the named registrations, the handset templates -- and none of it is a thing
   * somebody does for themselves. The API already allows the code on one's own extension
   * (extension:write:own, app/routes.ts), so this offers exactly what it will grant.
   */
  selfService?: boolean;
}) {
  const tenant = useTenant(tenantId);
  /**
   * The app is a paid feature of the seat, and the API refuses to enrol a phone without it
   * (app.mobile_app_disabled). Offering a sign-in code that cannot be redeemed is worse than not
   * offering one (owner, 20 September 2026), so the card is replaced by a line saying why.
   * Undefined while it loads: neither is shown, rather than one flashing into the other.
   */
  const mobileApp = useExtensionFeature(tenantId, ext.id, 'mobile_app');
  const mayPackage = useSession((s) => s.can)('extension:package:write');

  return (
    <>
      {mobileApp === true && (
        <div className="arc-card" style={{ marginBottom: 14 }}>
          <CardHead title="ArcSIP app" sub="a phone signs in with a code or its owner's TMS login" />
          <div className="arc-card-body">
            <AppSignIn tenantId={tenantId} ext={ext} />
          </div>
        </div>
      )}
      {mobileApp === false && (
        <div className="arc-card" style={{ marginBottom: 14 }}>
          <CardHead title="ArcSIP app" />
          <div className="arc-card-body arc-muted" style={{ fontSize: 13 }}>
            Not included in this extension's type
            {ext.extension_type_name ? ` (${ext.extension_type_name})` : ''}.
            {mayPackage ? ' Add the mobile app on the Features tab.' : ' Ask your provider to add it.'}
          </div>
        </div>
      )}

      {selfService ? null : <>
      <div className="arc-card" style={{ marginBottom: 14 }}>
        <CardHead title="SIP details" sub="for a softphone or a phone set up by hand" />
        <div className="arc-card-body">
          <SipCredential tenantId={tenantId} extensionId={ext.id} number={ext.number}
            domain={tenant.data?.sip_domain ?? null} server={tenant.data?.sip_registrar_host ?? null} />
          <MobileProvisioning tenantId={tenantId} ext={ext} />
        </div>
      </div>

      <AdditionalRegistrations tenantId={tenantId} ext={ext} />

      <ExtensionProvisioningTab tenantId={tenantId} ext={ext} />
      </>}
    </>
  );
}

/**
 * How current the answer is.
 *
 * Registration is ephemeral state cached from a node push. A "registered" badge
 * from a ten-minute-old snapshot is a lie, and staleness is precisely the
 * question when someone says their phone stopped working.
 */
function SnapshotAge({ at }: { at: string }) {
  const ageSec = Math.round((Date.now() - new Date(at).getTime()) / 1000);
  const stale = ageSec > 120;
  return (
    <span className={stale ? 'arc-badge arc-badge-warn' : 'arc-subtle'}
      style={stale ? undefined : { fontSize: 11.5 }}>
      {stale && <Icon name="warning" size={10} />}
      {ageSec < 60 ? `updated ${ageSec}s ago` : `updated ${Math.round(ageSec / 60)}m ago`}
    </span>
  );
}

interface SignIns {
  available: boolean;
  window_hours: number;
  bans_taken_at: string | null;
  sources: Array<{
    src_ip: string; failures: number; probes: number;
    first_at: string; last_at: string;
    blocked_in: string[]; trusted: boolean;
  }>;
}

const WHEN = (at: string) => new Date(at).toLocaleString('en-ZA', {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
});

/**
 * Is this phone registered, and if not, why not.
 *
 * Arranged around the diagnosis rather than the data: one sentence that says
 * what state the seat is in and what it means for calls, then the facts that
 * explain it, then sign-in attempts -- the evidence that was missing when a
 * phone "just would not register" and the reason was a wrong password retrying
 * twelve times a minute from an address the firewall had blocked, or trusts and
 * never will.
 *
 * Part of Troubleshoot. The SIP account and the handset list that used to sit
 * beside it are in Phone setup, which is where somebody goes to change them.
 */
export function RegistrationStatus({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  // Opens the event stream for as long as this is mounted. On a register or
  // unregister the row refetches in about a second instead of on the next sweep.
  const live = useRealtime(tenantId);
  const signIns = useQuery({
    queryKey: ['tenants', tenantId, 'extensions', ext.id, 'sign-in-attempts'] as const,
    queryFn: () => api.get<SignIns>(`/v1/tenants/${tenantId}/extensions/${ext.id}/sign-in-attempts`),
    refetchInterval: 30_000,
  });

  const reg = ext.registration;
  const unreachable = !!reg?.registered && /unreach|fail/i.test(reg.ping_status ?? '');

  /**
   * One sentence per state. Registered-but-unreachable is its own state, not a
   * footnote: the handset shows a line key either way, and calls to it ring
   * nowhere until it re-registers.
   */
  const summary = reg === undefined
    ? { tone: 'muted' as const, label: 'no report',
        text: 'No switch has reported on this extension — different from not registered: the switch may not be enrolled, or has not sent a snapshot yet.' }
    : !reg.registered
      ? { tone: 'danger' as const, label: 'offline',
          text: `Not registered${reg.offline_since ? ` since ${WHEN(reg.offline_since)} (${relativeTime(reg.offline_since)})` : ''}, so calls to it cannot reach a handset. The details below are what it last reported.` }
      : unreachable
        ? { tone: 'danger' as const, label: 'unreachable',
            text: 'Registered but not answering keep-alives, so calls ring nowhere until it registers again. Usually a router that stopped forwarding to the phone.' }
        : { tone: 'ok' as const, label: 'registered',
            text: `Registered${reg.contact_ip ? ` from ${reg.contact_ip}` : ''}${reg.user_agent ? ` on ${reg.user_agent}` : ''}.` };

  return (
    <>
      <div className="arc-card" style={{ marginBottom: 12 }}>
        <div className="arc-card-head">
          <h2 className="arc-h2">Registration</h2>
          {/* Only when the live stream is down: a "live" pill is only news when
              it is absent, so staleness carries the whole signal. */}
          {live !== 'live' && reg && <SnapshotAge at={reg.snapshot_at} />}
        </div>
        <div className="arc-card-body">
          <div className="arc-row" style={{ gap: 12, alignItems: 'flex-start' }}>
            <Badge tone={summary.tone}>
              <span className="arc-dot" aria-hidden="true" />{summary.label}
            </Badge>
            <span style={{ lineHeight: 1.5 }}>{summary.text}</span>
          </div>

          {reg !== undefined && (
            <dl className="arc-kv" style={{ marginTop: 14 }}>
              <dt>Last seen</dt>
              <dd>
                {reg.last_seen_at
                  ? <>{relativeTime(reg.last_seen_at)}
                      <span className="arc-subtle" style={{ marginLeft: 8 }}>{WHEN(reg.last_seen_at)}</span></>
                  : <span className="arc-subtle">never</span>}
              </dd>
              <dt>{reg.registered ? 'Handset address' : 'Last address'}</dt>
              <dd className="arc-mono">
                {/* The device's own address, from its Contact header. The
                    switch's network peer is the proxy, which would put every
                    extension on one IP. */}
                {reg.contact_ip ? `${reg.contact_ip}${reg.contact_port ? ':' + reg.contact_port : ''}` : '—'}
              </dd>
              <dt>Handset</dt>
              <dd>{reg.user_agent ?? <span className="arc-subtle">unknown</span>}</dd>
              <dt>Transport</dt>
              <dd>{reg.transport?.toUpperCase() ?? '—'}</dd>
              <dt>Keep-alive</dt>
              <dd>
                {reg.ping_status
                  ? <>
                      <Badge tone={/unreach|fail/i.test(reg.ping_status) ? 'danger' : 'ok'}>
                        {reg.ping_status === 'push' ? 'push service' : reg.ping_status.toLowerCase()}
                      </Badge>
                      {reg.ping_ms != null && (
                        <span className="arc-num" style={{ marginLeft: 8 }}>{reg.ping_ms} ms</span>
                      )}
                    </>
                  : <span className="arc-subtle">{reg.registered ? 'not probed yet' : 'offline'}</span>}
              </dd>
              {reg.registered && (
                <>
                  <dt>Registration expires</dt>
                  <dd>{reg.expires_at ? `${new Date(reg.expires_at).toLocaleTimeString('en-ZA')} (${relativeTime(reg.expires_at)})` : '—'}</dd>
                </>
              )}
              {reg.first_seen_at && (
                <>
                  <dt>First seen</dt>
                  <dd className="arc-muted">{new Date(reg.first_seen_at).toLocaleDateString('en-ZA', {
                    day: '2-digit', month: 'short', year: 'numeric' })}</dd>
                </>
              )}
              <dt>Contact URI</dt>
              <dd className="arc-mono arc-truncate" title={reg.contact ?? ''}>{reg.contact ?? '—'}</dd>
            </dl>
          )}
        </div>
      </div>

      <div className="arc-card" style={{ marginBottom: 12 }}>
        <CardHead title="Sign-in attempts" sub={<>failed, last {signIns.data?.window_hours ?? 24} hours</>} />
        {signIns.error ? (
          <div className="arc-card-body"><ErrorBanner error={signIns.error} /></div>
        ) : !signIns.data ? (
          <div className="arc-card-body"><div className="arc-skeleton" style={{ height: 40 }} /></div>
        ) : !signIns.data.available ? (
          <div className="arc-card-body">
            <span className="arc-subtle">The switch has not reported sign-in attempts.</span>
          </div>
        ) : signIns.data.sources.length === 0 ? (
          <Empty icon="check" title="No failed sign-ins" />
        ) : (
          <>
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead>
                  <tr><th>From</th><th className="arc-td-right">Failed</th><th>Last attempt</th><th>Firewall</th></tr>
                </thead>
                <tbody>
                  {signIns.data.sources.map((s) => (
                    <tr key={s.src_ip}>
                      <td className="arc-mono">{s.src_ip}</td>
                      <td className="arc-td-right arc-num">
                        {s.failures}
                        {s.probes > 0 && <span className="arc-subtle"> · {s.probes} unanswered</span>}
                      </td>
                      <td className="arc-muted">{relativeTime(s.last_at)}</td>
                      <td>
                        {s.blocked_in.length > 0
                          ? <Badge tone="danger">blocked</Badge>
                          : s.trusted
                            ? <Badge tone="info">trusted, never blocked</Badge>
                            : <span className="arc-subtle">not blocked</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="arc-card-body" style={{ paddingTop: 10 }}>
              <p className="arc-hint" style={{ margin: 0 }}>
                A failed sign-in means a device at that address has the wrong password for this
                extension.
                {signIns.data.sources.some((s) => s.blocked_in.length > 0)
                  && ' A blocked address is ignored completely until the block expires, so it cannot register even with the right password.'}
                {signIns.data.sources.some((s) => s.trusted && s.failures > 0)
                  && ' A trusted address is never blocked, so it keeps retrying until its password is corrected.'}
              </p>
            </div>
          </>
        )}
      </div>
    </>
  );
}
